import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { getEstimate, type EstimateFull } from '../services/estimateService';
import EasyEstimatePrzedmiarTab from '../components/easyEstimate/EasyEstimatePrzedmiarTab';
import { consumeVerifiedPositionsPageGrant } from '../utils/easyEstimateVerifiedIntent';

/**
 * Tylko pozycje zatwierdzone (verified) — ta sama siatka co Kosztorys, bez wersji/działów i bez akcji w wierszach.
 */
const EasyEstimateVerifiedPositionsPage: React.FC = () => {
  const { estimateId } = useParams<{ estimateId: string }>();
  const navigate = useNavigate();
  const [estimate, setEstimate] = React.useState<EstimateFull | null>(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    if (!estimateId) {
      navigate('/app/easy-estimate');
      return;
    }
    const id = Number(estimateId);
    if (!Number.isFinite(id)) {
      navigate('/app/easy-estimate');
      return;
    }
    setLoading(true);
    try {
      const full = await getEstimate(id);
      setEstimate(full);
    } catch {
      navigate('/app/easy-estimate');
    } finally {
      setLoading(false);
    }
  }, [estimateId, navigate]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (!estimateId) return;
    if (!consumeVerifiedPositionsPageGrant(estimateId)) {
      navigate(`/app/easy-estimate/estimate/${estimateId}`, { replace: true });
    }
  }, [estimateId, navigate]);

  if (loading) {
    return (
      <Box sx={{ p: 2, flex: 1, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#5F6368' }}>
        Ładowanie…
      </Box>
    );
  }

  if (!estimate) {
    return null;
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #e8eaed',
        }}
      >
        <IconButton
          size="small"
          aria-label="Wróć do kosztorysu"
          onClick={() => navigate(`/app/easy-estimate/estimate/${estimateId}`)}
          sx={{ color: '#020242' }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '18px', fontWeight: 500, color: '#020242' }}>
          Pozycje zweryfikowane
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <EasyEstimatePrzedmiarTab
          estimate={estimate}
          kosztorysTabActive
          roboczogodzinaZl={String(estimate.rg ?? '')}
          layoutVariant="verifiedList"
        />
      </Box>
    </Box>
  );
};

export default EasyEstimateVerifiedPositionsPage;
